import './policy-configuration-step-4.scss'

import { Image } from 'react-bootstrap'
import { Trans } from 'react-i18next'
import {
  RadioEmptyEnabledIcon,
  RadioFullEnabledIcon,
  SmartphoneIcon,
  WarningIcon,
} from 'src/assets'
import { PolicyConfigurationProps } from 'src/types'
import { Button, InputCheckbox, InputSelect, InputText } from 'src/ui/widgets'

import { cleanIban } from '../handlers/validation-schemas.hook'
import { usePolicyConfigurationStep4 } from './policy-configuration-step-4.hook'

const Sources = {
  'full-enabled': RadioFullEnabledIcon,
  'empty-enabled': RadioEmptyEnabledIcon,
}

export const PolicyConfigurationStep4 = ({
  formik,
  validationSchema,
}: PolicyConfigurationProps) => {
  const {
    t,
    paymentMethodsOptions,
    bankProducts,
    paymentTypes,
    isPisAlive,
    insured,
    paymentId,
    hasBankProducts,
    pisTermsConditionsUrl,
    pisPrivacyUrl,
    onPisPaymentSubmit,
  } = usePolicyConfigurationStep4({ formik, validationSchema })

  const { values, touched, errors, handleChange, handleBlur, setFieldValue, setFieldTouched } =
    formik

  const Title = <h3 className="mb-5 fw-light font-32">{t('policy_configuration.step_4.title')}</h3>

  const CustomRadioOption = (id: string, optionValue: string, formikValue: string) => {
    return (
      <label className="d-flex align-items-center">
        <input
          type="radio"
          value={id}
          checked={formikValue === id}
          name="paymentMethod"
          onChange={handleChange}
          onBlur={handleBlur}
          hidden
        />
        <Image
          src={Sources[`${formikValue === id ? 'full' : 'empty'}-${'enabled'}`]}
          width={18}
          height={18}
          className="m-0 p-0 me-2"
        />
        <h5 className="c-7f7f7f text-uppercase fw-bold font-12-roman mb-0">{optionValue}</h5>
      </label>
    )
  }

  const CardPayment = (
    <div className="col-12 d-flex flex-column align-items-center">
      <div className="col-12 col-md-8">
        <div id="my-axepta-sdk-pg" />
      </div>
    </div>
  )

  const MyBankPayment = (
    <div className="col-12 d-flex flex-column align-items-center">
      <div className="col-12 col-md-8">
        <div id="my-axepta-sdk-pg" />
      </div>
    </div>
  )

  const PisPayment = (
    <div className="col-12 d-flex flex-column align-items-center my-3">
      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3">
        <p className="fw-light">
          {t('policy_configuration.step_4.payment_options_data.pis_info')}
          <span className="fw-bold">{insured?.email}</span>
        </p>
      </div>
      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3">
        <InputText
          name="iban"
          handleChange={handleChange}
          touched={touched?.iban}
          value={cleanIban(values?.iban) || ''}
          error={errors?.iban}
          label={`${t('policy_configuration.step_4.payment_options_data.iban')}*`}
          placeholder={`${t('policy_configuration.step_4.payment_options_data.placeholder_iban')}*`}
          classes="mb-4"
        />
      </div>
      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3">
        <InputSelect
          name="bankProduct"
          options={bankProducts}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          validationSchema={validationSchema}
          value={values?.bankProduct || ''}
          error={errors?.bankProduct || ''}
          label={t('policy_configuration.step_4.payment_options_data.bank_product')}
          classes="mb-4"
          disabled={formik.getFieldMeta('iban').error !== undefined || !hasBankProducts}
        />
      </div>
      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3">
        <InputSelect
          name="paymentType"
          options={paymentTypes}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          validationSchema={validationSchema}
          value={values?.paymentType || ''}
          error={errors?.paymentType || ''}
          label={t('policy_configuration.step_4.payment_options_data.payment_type')}
          classes="mb-4"
          disabled={formik.getFieldMeta('iban').error !== undefined || !hasBankProducts}
        />
      </div>
      {values.paymentType === 'SCT-IT' && (
        <div className="col-12 col-md-8 mb-4 p-0 pe-md-3">
          <div className="warning-message d-md-flex justify-content-between align-items-center p-4">
            <div className="d-md-flex align-items-center">
              <Image src={WarningIcon} className={`warning-icon ms-0 me-3 filter-7f7f7f`} />
              <div>
                <label className="mb-2 font-16-bold d-flex align-items-center">
                  {t('policy_configuration.step_4.payment_options_data.bank_payment_info1')}
                </label>
                <label className="m-0 font-14 d-flex align-items-center">
                  {t('policy_configuration.step_4.payment_options_data.bank_payment_info2')}
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3">
        <InputCheckbox
          name="consentPayment"
          handleChange={handleChange}
          handleBlur={handleBlur}
          validationSchema={validationSchema}
          touched={touched?.consentPayment}
          value={values?.consentPayment}
          error={touched?.consentPayment && errors?.consentPayment ? errors.consentPayment : ''}
          label={t('policy_configuration.step_4.payment_options_data.consent_label')}
          classesLabel="m-0 font-16-bold d-flex align-items-center"
        >
          <div className="ms-4 ps-1 font-14 c-7f7f7f">
            <p>
              <Trans i18nKey="policy_configuration.step_4.payment_options_data.consent_description_1" />
              <a href={pisTermsConditionsUrl} className="c-7c2d35">
                {t('policy_configuration.step_4.payment_options_data.consent_link_1')}
              </a>
              <Trans i18nKey="policy_configuration.step_4.payment_options_data.consent_description_2" />
              <a href={pisPrivacyUrl} className="c-7c2d35">
                {t('policy_configuration.step_4.payment_options_data.consent_link_2')}
              </a>
            </p>
          </div>
        </InputCheckbox>
      </div>

      <div className="col-12 col-md-8 mb-4 mb-md-auto p-0 pe-md-3 d-flex justify-content-center">
        <Button
          type="submit"
          text={`${t('policy_configuration.step_4.payment')}`}
          disabled={formik.isValid === false}
          onPress={onPisPaymentSubmit}
          classes="bg-7c2d35 p-3 justify-content-center"
          classesText="text-uppercase font-16 c-ffffff"
        />
      </div>
    </div>
  )

  const MobileCheckAlert = (
    <div className="border font-16-roman p-4 bg-ffffff">
      <div className="warning-message d-md-flex justify-content-between align-items-center p-4">
        <div className="d-md-flex align-items-center">
          <Image src={SmartphoneIcon} className={`warning-icon ms-0 me-3 filter-7f7f7f`} />
          <div>
            <label className="mb-2 font-16-bold d-flex align-items-center">
              {t('policy_configuration.step_4.payment_options_data.card_payment_info1')}
            </label>
            <label className="m-0 font-14 d-flex align-items-center">
              {t('policy_configuration.step_4.payment_options_data.card_payment_info2')}
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div id="payment-container">
      {Title}
      <div className="border font-16-roman p-4 bg-ffffff mb-4">
        {CustomRadioOption(
          paymentMethodsOptions[0].id,
          paymentMethodsOptions[0].value,
          values.paymentMethod
        )}
        {values.paymentMethod === 'cardMethod' && paymentId && CardPayment}
      </div>
      <div className="border font-16-roman p-4 bg-ffffff mb-4">
        {CustomRadioOption(
          paymentMethodsOptions[1].id,
          paymentMethodsOptions[1].value,
          values.paymentMethod
        )}
        {values.paymentMethod === 'bankTransferMethod' && isPisAlive === true && PisPayment}
        {values.paymentMethod === 'bankTransferMethod' &&
          isPisAlive === false &&
          paymentId &&
          MyBankPayment}
      </div>
      {MobileCheckAlert}
    </div>
  )
}
